import { useCallback, useEffect } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import { atom, useAtom } from 'jotai'
import { jwtDecode } from 'jwt-decode'

import { userQueryOptions } from '@/features/users'
import { useAnalytics } from '@/utils/analytics'
import { getUserDetailsFromCurrentToken, isTokenValid } from '@/utils/auth'
import { addRumScope, removeRumScope } from '@/utils/monitoring'
import { removeItem, removeSessionItem, setItem, setSessionItem } from '@/utils/storage'

import { tokenAtom } from './auth.store'
import { useCustomerAccountData } from './useCustomerAccountData'
import { useLogin } from './useLogin'
import { useLogout } from './useLogout'
import { mapAccountResponseToCustomer } from '../domain/customer'

const extractPlainTokenFromAuthenticationResponse = (token: string): string => token.replace(/^JWT /, '').trim()

type LoginProps = {
  email: string
  password: string
}

type JwtTokenData = {
  customerId: string
  userId: string
  loggedInAsCustomer?: boolean
  tokenRequestedFrom?: string
}

const isAuthenticatedAtom = atom<boolean>(false)
const loggedInAsCustomerAtom = atom<boolean>(false)
const tokenRequestedFromAtom = atom<string | undefined>('')

export const useAuth = () => {
  const [token, setToken] = useAtom(tokenAtom)
  const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom)
  const [loggedInAsCustomer, setLoggedInAsCustomer] = useAtom(loggedInAsCustomerAtom)
  const [tokenRequestedFrom, setTokenRequestedFrom] = useAtom(tokenRequestedFromAtom)
  const loginMutation = useLogin()
  const logoutMutation = useLogout()
  const customerAccountData = useCustomerAccountData()
  const analyticsService = useAnalytics()
  const { isAuthenticated: isAuth0Authenticated, logout: auth0Logout } = useAuth0()

  const unAuthenticate = useCallback(() => {
    setLoggedInAsCustomer(false)
    setIsAuthenticated(false)
    setToken(undefined)
    removeItem('token')
    removeSessionItem('token')
    removeRumScope()
    analyticsService.resetAnalytics()
  }, [analyticsService, setIsAuthenticated, setLoggedInAsCustomer, setToken])

  const authenticate = useCallback(
    (token: string) => {
      const tokenData = jwtDecode<JwtTokenData>(token)
      const isImpersonating = tokenData.loggedInAsCustomer ?? false

      setIsAuthenticated(true)
      setToken(token)
      setItem('token', token)
      if (isImpersonating) {
        setSessionItem('token', token)
      }
      setLoggedInAsCustomer(isImpersonating)
      setTokenRequestedFrom(tokenData.tokenRequestedFrom)
    },
    [setIsAuthenticated, setLoggedInAsCustomer, setToken, setTokenRequestedFrom]
  )

  const loginAsCustomer = async (token: string) => {
    setLoggedInAsCustomer(true)
    authenticate(token)
    await fetchCustomerAccountData()
  }

  const fetchCustomerAccountData = useCallback(async () => {
    await customerAccountData.refetch()
  }, [customerAccountData])

  const login = useCallback(
    async (body: LoginProps) => {
      await loginMutation.mutateAsync(
        {
          email: body.email,
          password: body.password,
        },
        {
          onSuccess: async (data) => {
            const token = extractPlainTokenFromAuthenticationResponse(data.token)
            authenticate(token)
            await fetchCustomerAccountData()
          },
        }
      )
    },
    [loginMutation, authenticate, fetchCustomerAccountData]
  )

  const logout = useCallback(async () => {
    await unAuthenticate()
    await logoutMutation.mutateAsync(undefined)
    if (isAuth0Authenticated) {
      auth0Logout({ logoutParams: { returnTo: `${window.origin}/login` } })
    }
  }, [auth0Logout, isAuth0Authenticated, logoutMutation, unAuthenticate])

  const userIdFromToken = getUserDetailsFromCurrentToken()?.userId
  const userQuery = useQuery({
    ...userQueryOptions(userIdFromToken ?? ''),
    enabled: !!userIdFromToken && isAuthenticated,
  })
  const user = userQuery.data
  const customer = customerAccountData.data ? mapAccountResponseToCustomer(customerAccountData.data) : undefined

  useEffect(() => {
    if (!isAuthenticated || !user || !customer) return
    addRumScope({
      id: user.id,
      email: user.email,
      company: customer.name,
      customerId: customer._id,
    })
    analyticsService.initAnalytics(customer, user, loggedInAsCustomer, tokenRequestedFrom)
  }, [isAuthenticated, user, customer, loggedInAsCustomer, tokenRequestedFrom, analyticsService])

  return {
    token,
    login,
    logout,
    authenticate,
    unAuthenticate,
    loginAsCustomer,
    isReady: !customerAccountData.isPending,
    isLoggingIn: loginMutation.isPending || (isAuthenticated && (!customer || !user)),
    isLoggedIn: isAuthenticated && !!customer && !!user,
    shouldHideLogin: customerAccountData.isPending || (!!token && isTokenValid(token)),
    customer,
    isCustomerActive: !!customer?.activatedAt && !customer?.deactivatedAt,
    user,
    isAuthenticated,
    loggedInAsCustomer,
    tokenRequestedFrom,
    isTokenValid: isTokenValid(token),
  }
}
